// Typed fetch helpers for the API. The Auth section is shared by everything;
// the Agents/Conversations/streaming sections below are DORMANT (chat
// feature, see CLAUDE.md) - kept for a later phase, unused by the admin
// portal's own pages. Admin-portal helpers are at the bottom of this file.
import {
  Advance,
  AdvanceAmount,
  AdvanceApproval,
  AdvanceStatus,
  Agent,
  AllowancePreview,
  AllowedCountry,
  AuditLogEntry,
  AuthUser,
  Approval,
  ApprovalStatus,
  ClaimMethod,
  Conversation,
  ConversationSummary,
  DuplicateVerdict,
  ExpenseCategory,
  ExtractedBillFields,
  FinanceQueueRow,
  OrgUser,
  OtherBillFieldValues,
  OtherClaimType,
  PolicySheet,
  TravelRequestStatus,
  TravelRequestSummary,
  TravelType,
} from './types';

const opts: RequestInit = { credentials: 'include' };

// Carries the parsed JSON error body (e.g. {"error": "..."} from a 409/400)
// so callers can show the backend's actual message instead of just the HTTP
// status - see assignRole's 409 for single-holder roles (db/roles_store.py).
export class ApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, body: any) {
    super(body?.error || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }
  return res.json() as Promise<T>;
}

// --- Auth ---
export async function fetchMe(): Promise<AuthUser | null> {
  const res = await fetch('/api/auth/me', opts);
  if (!res.ok) return null;
  const data = await res.json();
  return data?.authenticated ? { name: data.name, email: data.email } : null;
}

export async function fetchLoginUrl(): Promise<string> {
  const data = await json<{ url: string }>(await fetch('/api/auth/login-url', opts));
  return data.url;
}

// Enriched profile from Microsoft Graph (name, jobTitle, department, …).
export async function fetchProfile(): Promise<Partial<AuthUser>> {
  const res = await fetch('/api/auth/profile', opts);
  if (!res.ok) return {};
  return res.json();
}

// Same-origin endpoint that proxies the user's Graph photo (404 → no photo).
export const PHOTO_URL = '/api/auth/photo';

export function logout(): Promise<Response> {
  return fetch('/api/auth/logout', { method: 'POST', ...opts });
}

// --- Agents ---
export async function fetchAgents(): Promise<Agent[]> {
  return json<Agent[]>(await fetch('/api/agents', opts));
}

// --- Conversations ---
export async function fetchConversations(): Promise<ConversationSummary[]> {
  return json<ConversationSummary[]>(await fetch('/api/conversations', opts));
}

export async function fetchConversation(id: string): Promise<Conversation> {
  return json<Conversation>(await fetch(`/api/conversations/${id}`, opts));
}

export async function createConversation(agentId: string): Promise<ConversationSummary> {
  return json<ConversationSummary>(
    await fetch('/api/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentId }),
      ...opts,
    }),
  );
}

export async function renameConversation(id: string, title: string): Promise<ConversationSummary> {
  return json<ConversationSummary>(
    await fetch(`/api/conversations/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
      ...opts,
    }),
  );
}

export async function deleteConversation(id: string): Promise<void> {
  await fetch(`/api/conversations/${id}`, { method: 'DELETE', ...opts });
}

// --- Streaming a reply ---
// Calls onToken for each chunk; resolves with the full text. Aborts via signal.
export async function streamMessage(
  conversationId: string,
  content: string,
  onToken: (text: string) => void,
  signal?: AbortSignal,
): Promise<string> {
  const res = await fetch(`/api/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
    signal,
    ...opts,
  });
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      const line = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const payload = JSON.parse(line.slice(5).trim());
      if (payload.type === 'token') {
        full += payload.text;
        onToken(payload.text);
      } else if (payload.type === 'error') {
        throw new Error(payload.error || 'Streaming error');
      }
    }
  }
  return full;
}

// ---------------------------------------------------------------------------
// Admin portal (active)
// ---------------------------------------------------------------------------

// --- My Requests / My Approvals (available to every authenticated user) ---
export async function fetchMyRequests(status?: TravelRequestStatus): Promise<TravelRequestSummary[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return json<TravelRequestSummary[]>(await fetch(`/api/my-requests${qs}`, opts));
}

/** Full request detail for the "View details" card an approver opens
 *  before deciding - readable by the requester or by whoever is the
 *  Manager/Finance approver on this specific request (see
 *  requests_api.py's get_travel_request). */
export async function fetchTravelRequest(travelRequestId: string): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(await fetch(`/api/travel-requests/${encodeURIComponent(travelRequestId)}`, opts));
}

/** The advance linked to this travel request (or null if there isn't one,
 *  or it can't be found) - request-scoped, NOT fetchAdvance's own scoping
 *  (requester / the advance's OWN approver). A Manager or Finance approver
 *  of the REQUEST can be a different person than the advance's own
 *  approver, so fetchAdvance 404s for them even though they're fully
 *  entitled to see what's linked - see requests_api.py's
 *  get_request_linked_advance for the full reasoning. Used by
 *  RequestDetailDialog's "Linked advance" card in place of fetchAdvance. */
export async function fetchRequestLinkedAdvance(travelRequestId: string): Promise<Advance | null> {
  const { advance } = await json<{ advance: Advance | null }>(
    await fetch(`/api/travel-requests/${encodeURIComponent(travelRequestId)}/linked-advance`, opts),
  );
  return advance;
}

/** The request's PDF report (summary + every attached bill). Sends the
 *  browser's UTC offset so decision dates print in the viewer's own time. */
export async function downloadRequestReport(
  travelRequestId: string,
): Promise<{ blob: Blob; filename: string }> {
  const tzOffset = -new Date().getTimezoneOffset();
  const res = await fetch(
    `/api/travel-requests/${encodeURIComponent(travelRequestId)}/report?tzOffset=${tzOffset}`,
    opts,
  );
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }
  const disposition = res.headers.get('Content-Disposition') || '';
  const filename =
    /filename="([^"]+)"/.exec(disposition)?.[1] ||
    `ASMARA-Request-${travelRequestId.slice(0, 8).toUpperCase()}.pdf`;
  return { blob: await res.blob(), filename };
}

export async function fetchMyApprovals(status?: ApprovalStatus): Promise<Approval[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return json<Approval[]>(await fetch(`/api/my-approvals${qs}`, opts));
}

/** decideApproval's response when decision is "Rejected" - the normal,
 *  already-final Approval record (rejecting is immediate, unlike
 *  Approved - see decideApproval's own docstring). */
export type DecideRejectResult = Approval;

/** decideApproval's response when decision is "Approved" - a PREVIEW
 *  only. Nothing is actually decided yet: the request is still Pending,
 *  no PendingFinance transition, no Finance-approval record created. This
 *  just resolves who the request would route to at Finance so
 *  MyApprovals.tsx can show its "Notify Finance" popup (To/Cc) before the
 *  real approval happens - that only fires once the popup's Send button
 *  calls approveAndNotifyFinance below. Closing the popup/refreshing
 *  before Send leaves the request exactly as it was: still Pending, safe
 *  to approve again. */
export interface ApprovePreviewResult {
  travelRequestId: string;
  financeApproverName: string | null;
  financeApproverEmail: string | null;
}

export async function decideApproval(
  travelRequestId: string,
  decision: 'Rejected',
  comment?: string,
): Promise<DecideRejectResult>;
export async function decideApproval(
  travelRequestId: string,
  decision: 'Approved',
  comment?: string,
): Promise<ApprovePreviewResult>;
export async function decideApproval(
  travelRequestId: string,
  decision: 'Approved' | 'Rejected',
  comment?: string,
): Promise<DecideRejectResult | ApprovePreviewResult> {
  return json(
    await fetch(`/api/my-approvals/${encodeURIComponent(travelRequestId)}/decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, comment }),
      ...opts,
    }),
  );
}

/** The ACTUAL Approve action - fired only when the Manager clicks Send on
 *  the "Notify Finance" popup shown after decideApproval("Approved")'s
 *  preview. One atomic call: decides the Manager stage, moves the request
 *  to PendingFinance, creates the Finance approval record, and sends both
 *  the Teams notification and the sign-off email (with the Manager's
 *  chosen Cc) together - all or nothing, so there's no window where the
 *  request is approved but nobody was actually told. */
export async function approveAndNotifyFinance(
  travelRequestId: string,
  comment?: string,
  ccEmails?: string[],
): Promise<Approval> {
  return json<Approval>(
    await fetch(`/api/my-approvals/${encodeURIComponent(travelRequestId)}/approve-and-notify-finance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment, ccEmails }),
      ...opts,
    }),
  );
}

/** Manager's Review action: save field edits, then (same preview-only
 *  shape as decideApproval's Approved branch) resolve who Finance would
 *  be - the actual commit is the SAME approveAndNotifyFinance call above,
 *  fired a second time once the Manager clicks Send on the popup this
 *  response feeds. */
export async function reviewApproval(
  travelRequestId: string,
  fields: object,
  comment?: string,
): Promise<ApprovePreviewResult> {
  return json<ApprovePreviewResult>(
    await fetch(`/api/my-approvals/${encodeURIComponent(travelRequestId)}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields, comment }),
      ...opts,
    }),
  );
}

/** Manager's Resubmit action: send the request back to the employee to
 *  fix - single-click commit, no preview step (there's no "notify
 *  Finance" moment on a resubmit). */
export async function resubmitApproval(travelRequestId: string, comment: string): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/my-approvals/${encodeURIComponent(travelRequestId)}/resubmit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment }),
      ...opts,
    }),
  );
}

/** The where-to part of a travel request or an advance - identical for both.
 *  The travel type decides which fields apply (see requests_api.py's
 *  _clean_trip_locations): Domestic sends `country` + `startCity` +
 *  `endCity`; International sends `originCountry` (starting country) +
 *  `country` (destination). `country` is always the one the policy rate is
 *  keyed by. */
export interface TripLocationPayload {
  travelType: TravelType;
  country: string;
  originCountry?: string;
  startCity?: string;
  endCity?: string;
}

export interface NewRequestPayload extends TripLocationPayload {
  department: string;
  departureAt: string; // ISO datetime
  arrivalAt: string;   // ISO datetime
  flightDetails?: string;
  currency: string;
  currencyAutoSelected: boolean;
  claimMethod: ClaimMethod;
  /** A Paid, unlinked advance the employee already has, of the same travel
   *  type AND the same claim method as this request - advances are
   *  raised/approved/paid separately, see the Advance helpers below. */
  linkedAdvanceId?: string;
  /** Reimbursement only: create as a Draft (no Manager approval yet) so
   *  the bills-upload step has a real request to attach expenses to.
   *  Finalize with finalizeMyRequest() once the employee actually submits. */
  saveAsDraft?: boolean;
  /** Who this request routes to once it reaches the Finance stage - the
   *  requester's own pick from fetchFinanceApprovers(), snapshotted
   *  server-side as financeApproverIdAtSubmission. Used whether the
   *  request goes Manager-approved -> Finance (the normal path) or
   *  straight to Finance (no manager on file) - see requests_api.py's
   *  decide_approval / _route_for_manager_approval. */
  financeApproverId?: string;
  /** Only meaningful when the requester has no reporting manager on file,
   *  so this request skips straight to PendingFinance with no separate
   *  Manager-approve step - the SAME "Notify Finance" Cc popup a Manager
   *  would otherwise see (FinanceNoticeDialog) is shown at submission time
   *  instead, and this is the requester's pick from it. Ignored
   *  server-side when there IS a manager. */
  ccEmails?: string[];
}

/** One current Finance role holder, for the New Request wizard's and
 *  Request Advance form's Finance Approver picker. Open to every
 *  authenticated employee (unlike the Admin-only /admin/users), since
 *  every requester needs this list to submit a request at all. */
export interface FinanceApprover {
  id: string;
  displayName: string | null;
  email: string | null;
}

export async function fetchFinanceApprovers(): Promise<FinanceApprover[]> {
  return json<FinanceApprover[]>(await fetch('/api/finance-approvers', opts));
}

/** One org user from the live Entra directory (falling back to UserStore's
 *  local cache if Graph is unavailable - see requests_api.py's
 *  list_org_users_route), for the "Notify Finance" popup's Cc picker
 *  (MyApprovals.tsx). */
export interface OrgUserLite {
  id: string;
  displayName: string | null;
  email: string | null;
}

export async function fetchOrgUsersForCc(): Promise<OrgUserLite[]> {
  return json<OrgUserLite[]>(await fetch('/api/org-users', opts));
}

export interface SubmitRequestResult {
  request: TravelRequestSummary;
  approval: Approval | null;
  /** True iff the requester has no reporting manager on file, so the
   *  Manager-approval stage was skipped entirely and this request went
   *  straight to PendingFinance (see requests_api.py's
   *  _route_for_manager_approval). Absent (not false) on the
   *  saveAsDraft=true response, which has no approval yet for an
   *  unrelated reason - only present once the request is actually
   *  submitted, by createMyRequest (non-Draft) or finalizeMyRequest. */
  skippedToFinance?: boolean;
}

export async function createMyRequest(payload: NewRequestPayload): Promise<SubmitRequestResult> {
  return json(
    await fetch('/api/my-requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      ...opts,
    }),
  );
}

/** financeApproverId: re-applied to the Draft before routing - the Finance
 *  Approver is picked on Step 1, before the Draft exists (created on
 *  reaching Bills), so going back and picking someone else afterwards must
 *  still reach the server at Submit time or the request routes to the
 *  ORIGINAL pick despite the Review summary showing the new one. Always
 *  send the current form value (undefined clears it, matching
 *  createMyRequest's own `|| undefined` convention), not just when it
 *  changed - the server treats this as "set to exactly this" each time. */
export async function finalizeMyRequest(
  travelRequestId: string,
  ccEmails?: string[],
  financeApproverId?: string,
): Promise<SubmitRequestResult> {
  return json(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}/finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ccEmails, financeApproverId }),
      ...opts,
    }),
  );
}

/** Deletes a Draft the employee decided not to finish - the "Discard"
 *  action on a Draft row in My Requests. Only valid while still Draft. */
export async function discardMyRequest(travelRequestId: string): Promise<void> {
  await json(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}`, { method: 'DELETE', ...opts }),
  );
}

/** Employee finishes editing a ResubmitRequested request and resubmits it -
 *  UNLIKE finalizeMyRequest, this does not create a new Manager approval:
 *  it routes back to whichever stage sent it back, reusing that stage's
 *  existing, untouched approval record. `fields` is the same wizard-shaped
 *  field set createMyRequest/finalizeMyRequest write. */
export async function resubmitMyRequest(
  travelRequestId: string,
  fields: object,
): Promise<{ request: TravelRequestSummary }> {
  return json(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}/resubmit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
      ...opts,
    }),
  );
}

/** Saves the employee's edits to a ResubmitRequested request without
 *  resubmitting it - claim method, trip, dates and linked advance. The
 *  server re-prices it (estimate, policy snapshot, bill flags) and returns
 *  the updated request. See requests_api.py's save_resubmit_edits. */
export async function saveResubmitEdits(
  travelRequestId: string,
  fields: object,
): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}/resubmit-edits`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
      ...opts,
    }),
  );
}

/** Links, swaps or unlinks (advanceId: null) the advance on a Draft that
 *  already exists on the server - a Reimbursement Draft is created, with its
 *  advance already marked linked, as soon as the wizard reaches the bills
 *  step, so changing the link after that has to be told to the server or the
 *  advance stays consumed. The advance the Draft previously held is released
 *  back to Paid/unlinked. See requests_api.py's set_draft_linked_advance. */
export async function setDraftLinkedAdvance(travelRequestId: string, advanceId: string | null): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}/linked-advance`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ advanceId }),
      ...opts,
    }),
  );
}

export async function previewAllowance(payload: {
  country: string;
  travelType: TravelType;
  departureAt: string;
  arrivalAt: string;
  claimMethod: ClaimMethod;
}): Promise<AllowancePreview> {
  return json<AllowancePreview>(
    await fetch('/api/my-requests/preview-allowance', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      ...opts,
    }),
  );
}

/** Countries the signed-in employee can pick for this travel type - only
 *  what an admin has actually configured (their designation's own row, or
 *  the org-wide default, at that travel type's rate). There's no fallback
 *  anymore, so the Country picker is restricted to exactly this list
 *  rather than every country in lib/countries.ts, and its Currency field is
 *  just a read-only display of whichever currency that country resolves to
 *  (see requests_api.py's list_allowed_countries).
 *
 *  Pass `claimMethod` from the request wizard (each scheme has its own
 *  list); omit it from the advance form, which isn't tied to one scheme and
 *  so gets every country either scheme offers. */
export async function fetchAllowedCountries(travelType: TravelType, claimMethod?: ClaimMethod): Promise<AllowedCountry[]> {
  const params = new URLSearchParams({ travelType });
  if (claimMethod) params.set('claimMethod', claimMethod);
  return json<AllowedCountry[]>(await fetch(`/api/my-policy/countries?${params.toString()}`, opts));
}

/** Request-scoped twin of fetchAllowedCountries, for ReviewRequestDialog -
 *  resolves the country list from the REQUESTER's designation, not the
 *  signed-in approver's (see requests_api.py's review_allowed_countries) -
 *  using the generic fetchAllowedCountries from Review could drop the
 *  request's own already-saved country out of the list and leave the
 *  picker showing its placeholder instead of the real value. */
export async function fetchReviewAllowedCountries(
  travelRequestId: string, travelType: TravelType, claimMethod?: ClaimMethod,
): Promise<AllowedCountry[]> {
  const params = new URLSearchParams({ travelType });
  if (claimMethod) params.set('claimMethod', claimMethod);
  return json<AllowedCountry[]>(
    await fetch(`/api/my-approvals/${encodeURIComponent(travelRequestId)}/allowed-countries?${params.toString()}`, opts),
  );
}

/** Advance-side twin of fetchReviewAllowedCountries, for ReviewAdvanceDialog. */
export async function fetchReviewAdvanceAllowedCountries(
  advanceId: string, travelType: TravelType, claimMethod?: ClaimMethod,
): Promise<AllowedCountry[]> {
  const params = new URLSearchParams({ travelType });
  if (claimMethod) params.set('claimMethod', claimMethod);
  return json<AllowedCountry[]>(
    await fetch(`/api/my-advance-approvals/${encodeURIComponent(advanceId)}/allowed-countries?${params.toString()}`, opts),
  );
}

/** Live Full/half-day + Estimated-amount preview for ReviewRequestDialog, as
 *  the approver edits dates/country/travel type - request-scoped twin of
 *  previewAllowance, same reasoning as fetchReviewAllowedCountries (resolves
 *  the REQUESTER's designationAtSubmission, never the signed-in approver's
 *  own). Read-only - never writes anything; the actual save-time recompute
 *  is requests_api.py's _recompute_review_estimate, which this mirrors
 *  exactly so what's shown here always matches what Save would store. Same
 *  route serves both the Manager stage (MyApprovals.tsx) and the Finance
 *  stage (ApproverQueue.tsx) - review_preview_estimate checks the
 *  signed-in user against EITHER the request's approval.approverId or its
 *  financeApproval.approverId. */
export async function fetchReviewPreviewEstimate(
  travelRequestId: string,
  payload: { country: string; travelType: TravelType; departureAt: string; arrivalAt: string },
): Promise<AllowancePreview> {
  return json<AllowancePreview>(
    await fetch(`/api/my-approvals/${encodeURIComponent(travelRequestId)}/preview-estimate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      ...opts,
    }),
  );
}

/** Live per-currency cap preview for ReviewAdvanceDialog, as the approver
 *  edits dates/country/travel type - request-scoped twin of
 *  fetchAdvanceLimit, same reasoning as fetchReviewAdvanceAllowedCountries
 *  (resolves the REQUESTER's designation, preferring designationAtSubmission,
 *  never the signed-in approver's own). No claimMethod param - unlike the
 *  employee's own Request Advance form, it isn't editable in Review (read
 *  from the advance itself server-side). */
export async function fetchReviewAdvancePreviewLimit(
  advanceId: string,
  payload: { country: string; travelType: TravelType; departureAt: string; arrivalAt: string },
): Promise<{
  currency: string | null;
  limit: number | null;
  limitsByCurrency: Record<string, number>;
}> {
  return json(
    await fetch(`/api/my-advance-approvals/${encodeURIComponent(advanceId)}/preview-limit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      ...opts,
    }),
  );
}

/** Tells the wizard's Claim Method step (before any trip details exist)
 *  whether the signed-in employee's designation has a default scheme, so
 *  the "reason required" field can appear immediately on that same step -
 *  create_my_request enforces the identical rule server-side, so this is
 *  purely an earlier warning, never the source of truth. */
export async function fetchDefaultClaimMethod(): Promise<ClaimMethod | null> {
  const data = await json<{ defaultClaimMethod: ClaimMethod | null }>(
    await fetch('/api/my-requests/default-claim-method', opts),
  );
  return data.defaultClaimMethod;
}

// --- Finance Queue (the second, Approver-role, approval stage) ---
export async function fetchFinanceQueue(status?: ApprovalStatus): Promise<FinanceQueueRow[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return json<FinanceQueueRow[]>(await fetch(`/api/finance-queue${qs}`, opts));
}

export async function decideFinanceApproval(
  travelRequestId: string,
  decision: 'Approved' | 'Rejected',
  comment?: string,
): Promise<Approval> {
  return json<Approval>(
    await fetch(`/api/finance-queue/${encodeURIComponent(travelRequestId)}/decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, comment }),
      ...opts,
    }),
  );
}

/** Finance's Review action: save field edits and approve in the same
 *  action - unlike the Manager stage, Finance's plain Approve has no
 *  preview popup today, so this returns the FINAL Approval record
 *  directly (same shape as decideFinanceApproval's own Approved result),
 *  not a preview. */
export async function reviewFinanceApproval(
  travelRequestId: string,
  fields: object,
  comment?: string,
): Promise<Approval> {
  return json<Approval>(
    await fetch(`/api/finance-queue/${encodeURIComponent(travelRequestId)}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields, comment }),
      ...opts,
    }),
  );
}

/** Finance's Resubmit action: send the request back to the employee to
 *  fix - single-click commit, same shape as the Manager stage's
 *  resubmitApproval. */
export async function resubmitFinanceApproval(travelRequestId: string, comment: string): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/finance-queue/${encodeURIComponent(travelRequestId)}/resubmit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment }),
      ...opts,
    }),
  );
}

/** `settledAmounts`: what Finance paid out, net of any advance, one line per
 *  currency paid. */
export async function settleRequest(
  travelRequestId: string,
  settledAmounts: AdvanceAmount[],
): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/finance-queue/${encodeURIComponent(travelRequestId)}/settle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ settledAmounts }),
      ...opts,
    }),
  );
}

/** A currency pair's conversion rate as of `date` (YYYY-MM-DD; omit for the
 *  latest rate) - null when Frankfurter has no coverage for the pair at all,
 *  or the lookup failed (see fx.py's get_rate - the backend already falls
 *  back to the latest rate when the exact date has none). */
export async function fetchFxRate(base: string, quote: string, date?: string): Promise<number | null> {
  const qs = new URLSearchParams({ base, quote, ...(date ? { date } : {}) });
  const { rate } = await json<{ rate: number | null }>(await fetch(`/api/fx/rate?${qs}`, opts));
  return rate;
}

// --- Advances (a standalone request, independent of any travel request) ---
export async function fetchMyAdvances(status?: AdvanceStatus): Promise<Advance[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return json<Advance[]>(await fetch(`/api/my-advances${qs}`, opts));
}

/** Full advance detail for the "View details" card an approver opens
 *  before deciding - readable by the requester or by the Manager approver
 *  on this specific advance (see requests_api.py's get_advance). */
export async function fetchAdvance(advanceId: string): Promise<Advance> {
  return json<Advance>(await fetch(`/api/advances/${encodeURIComponent(advanceId)}`, opts));
}

/** The signed-in employee's own Paid, not-yet-linked advances - backs the
 *  wizard's "link an advance you received" picker. Pass the request's
 *  travel type to see only advances raised for that type (a Domestic
 *  request offers Domestic advances, an International one International),
 *  and its claim method to see only advances raised (or capped) for that
 *  same scheme - see requests_api.py's _advance_matches_claim_method. */
export async function fetchLinkableAdvances(travelType?: TravelType, claimMethod?: ClaimMethod): Promise<Advance[]> {
  const params = new URLSearchParams();
  if (travelType) params.set('travelType', travelType);
  if (claimMethod) params.set('claimMethod', claimMethod);
  const qs = params.toString();
  return json<Advance[]>(await fetch(`/api/my-advances/linkable${qs ? `?${qs}` : ''}`, opts));
}

/** The most an advance may be for on the trip entered so far, under the
 *  claim method picked for it - that scheme's entitlement for the given
 *  country and travel type (see requests_api.py's _advance_entitlement).
 *  Rejects with an ApiError carrying the admin-facing reason when nothing
 *  is configured. */
export async function fetchAdvanceLimit(payload: {
  travelType: TravelType;
  claimMethod: ClaimMethod;
  country: string;
  departureAt: string;
  arrivalAt: string;
}): Promise<{
  /** The trip's own policy currency (the advance form's default row). */
  currency: string;
  limit: number;
  /** The cap for every currency an advance row may use - its keys are
   *  exactly the currencies the policy configures for this employee +
   *  claim method + trip type (see requests_api.py's _advance_caps). */
  limitsByCurrency: Record<string, number>;
}> {
  return json(
    await fetch('/api/my-advances/preview-limit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      ...opts,
    }),
  );
}

export async function createAdvance(
  payload: TripLocationPayload & {
    /** One line per currency requested. Only the line in the trip's policy
     *  currency is capped (see requests_api.py's _clean_advance_amounts). */
    amounts: AdvanceAmount[];
    /** Fixed Allowance or Reimbursement - same Step 1 choice the request
     *  wizard makes, now made up front for an advance too (see
     *  requests_api.py's create_advance). Decides which scheme's rate caps
     *  this advance and which scheme's requests it can later link to. */
    claimMethod: ClaimMethod;
    reason?: string;
    departureAt: string;
    arrivalAt: string;
    flightDetails?: string;
    /** Same Finance Approver pick as NewRequestPayload - only meaningful
     *  when the requester has no reporting manager on file (create_advance
     *  falls back straight to Finance in that case; otherwise it's ignored
     *  server-side, the advance only ever needing Manager approval before
     *  disbursement). */
    financeApproverId?: string;
    /** Same "Notify Finance" Cc popup pick as NewRequestPayload.ccEmails,
     *  shown at submission time for a manager-less requester. Ignored
     *  server-side when there IS a manager. */
    ccEmails?: string[];
  },
): Promise<{
  advance: Advance;
  approval: AdvanceApproval | null;
  /** True iff the requester has no reporting manager on file, so this
   *  advance's approval was routed straight to the chosen Finance Approver
   *  instead of a Manager (see requests_api.py's create_advance). */
  skippedToFinance: boolean;
}> {
  return json(
    await fetch('/api/my-advances', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      ...opts,
    }),
  );
}

// --- Other reimbursements (City travel / Miscellaneous) ---
// Same request/advance documents as travel (requestKind "other"); after
// creation every travel route is reused - bills, finalizeMyRequest,
// discardMyRequest, detail, report, approvals and settlement.
export async function fetchMyOtherRequests(status?: TravelRequestStatus): Promise<TravelRequestSummary[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return json<TravelRequestSummary[]>(await fetch(`/api/my-other-requests${qs}`, opts));
}

/** The Other wizard's header - claim type, country (sets the default bill
 *  currency), purpose and Finance Approver. */
export interface OtherRequestHeader {
  country: string;
  currency: string;
  purpose: string;
  financeApproverId?: string;
}

/** Creates an Other reimbursement Draft (always a Draft - submitted later
 *  with finalizeMyRequest). */
export async function createMyOtherRequest(
  payload: OtherRequestHeader & { claimType: OtherClaimType; linkedAdvanceId?: string },
): Promise<{ request: TravelRequestSummary }> {
  return json(
    await fetch('/api/my-other-requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      ...opts,
    }),
  );
}

/** Saves the header fields sent on a Draft (or a request sent back for
 *  changes, where the Finance Approver can't change). */
export async function updateMyOtherRequest(
  travelRequestId: string,
  fields: Partial<OtherRequestHeader>,
): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/my-other-requests/${encodeURIComponent(travelRequestId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
      ...opts,
    }),
  );
}

export async function setOtherLinkedAdvance(travelRequestId: string, advanceId: string | null): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/my-other-requests/${encodeURIComponent(travelRequestId)}/linked-advance`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ advanceId }),
      ...opts,
    }),
  );
}

export async function resubmitMyOtherRequest(
  travelRequestId: string,
  fields: Partial<Omit<OtherRequestHeader, 'financeApproverId'>>,
): Promise<{ request: TravelRequestSummary }> {
  return json(
    await fetch(`/api/my-other-requests/${encodeURIComponent(travelRequestId)}/resubmit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
      ...opts,
    }),
  );
}

export async function fetchMyOtherAdvances(status?: AdvanceStatus): Promise<Advance[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return json<Advance[]>(await fetch(`/api/my-other-advances${qs}`, opts));
}

/** The employee's own Paid Other advances available to link to a request
 *  of this claim type. */
export async function fetchLinkableOtherAdvances(claimType: OtherClaimType): Promise<Advance[]> {
  return json<Advance[]>(
    await fetch(`/api/my-other-advances/linkable?claimType=${encodeURIComponent(claimType)}`, opts),
  );
}

/** An Other advance - claim type, Finance Approver, purpose (its name,
 *  required) and one or more uncapped currency amounts. Routed like a
 *  travel advance (see createAdvance). */
export async function createOtherAdvance(payload: {
  claimType: OtherClaimType;
  purpose: string;
  amounts: AdvanceAmount[];
  advanceDate?: string;
  financeApproverId?: string;
  ccEmails?: string[];
}): Promise<{ advance: Advance; approval: AdvanceApproval | null; skippedToFinance: boolean }> {
  return json(
    await fetch('/api/my-other-advances', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      ...opts,
    }),
  );
}

export async function fetchMyAdvanceApprovals(status?: ApprovalStatus): Promise<AdvanceApproval[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return json<AdvanceApproval[]>(await fetch(`/api/my-advance-approvals${qs}`, opts));
}

/** decideAdvanceApproval's response when decision is "Approved" - a
 *  PREVIEW only, mirroring decideApproval's travel-request equivalent
 *  exactly: nothing is decided yet, this just resolves the Finance
 *  Approver the requester picked at submission time so MyApprovals.tsx
 *  can show its "Notify Finance" popup before the real approval happens -
 *  that only fires once the popup's Send button calls
 *  approveAdvanceAndNotifyFinance below. */
export interface ApproveAdvancePreviewResult {
  advanceId: string;
  financeApproverName: string | null;
  financeApproverEmail: string | null;
}

export async function decideAdvanceApproval(
  advanceId: string,
  decision: 'Rejected',
  comment?: string,
): Promise<AdvanceApproval>;
export async function decideAdvanceApproval(
  advanceId: string,
  decision: 'Approved',
  comment?: string,
): Promise<ApproveAdvancePreviewResult>;
export async function decideAdvanceApproval(
  advanceId: string,
  decision: 'Approved' | 'Rejected',
  comment?: string,
): Promise<AdvanceApproval | ApproveAdvancePreviewResult> {
  return json(
    await fetch(`/api/my-advance-approvals/${encodeURIComponent(advanceId)}/decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, comment }),
      ...opts,
    }),
  );
}

/** The ACTUAL Approve action for an advance - fired only when the
 *  Manager clicks Send on the "Notify Finance" popup shown after
 *  decideAdvanceApproval("Approved")'s preview. One atomic call: decides
 *  the Manager stage, notifies the employee, and sends both the Teams
 *  notification and the sign-off email (with the Manager's chosen Cc) to
 *  the Finance Approver together - all or nothing. */
export async function approveAdvanceAndNotifyFinance(
  advanceId: string,
  comment?: string,
  ccEmails?: string[],
): Promise<AdvanceApproval> {
  return json<AdvanceApproval>(
    await fetch(`/api/my-advance-approvals/${encodeURIComponent(advanceId)}/approve-and-notify-finance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment, ccEmails }),
      ...opts,
    }),
  );
}

/** Manager's Review action on an advance: save field edits, then (same
 *  preview-only shape as decideAdvanceApproval's Approved branch) resolve
 *  who Finance would be - the actual commit is the SAME
 *  approveAdvanceAndNotifyFinance call above, fired a second time once the
 *  Manager clicks Send on the popup this response feeds. */
export async function reviewAdvanceApproval(
  advanceId: string,
  fields: object,
  comment?: string,
): Promise<ApproveAdvancePreviewResult> {
  return json<ApproveAdvancePreviewResult>(
    await fetch(`/api/my-advance-approvals/${encodeURIComponent(advanceId)}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields, comment }),
      ...opts,
    }),
  );
}

/** Manager's Resubmit action on an advance: send it back to the employee
 *  to fix - single-click commit, no preview step. */
export async function resubmitAdvanceApproval(advanceId: string, comment: string): Promise<Advance> {
  return json<Advance>(
    await fetch(`/api/my-advance-approvals/${encodeURIComponent(advanceId)}/resubmit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment }),
      ...opts,
    }),
  );
}

/** Finance's one-click action for a Pending advance that skipped straight to
 *  them (no reporting manager on file) - decides the Manager-stage approval
 *  AND marks it Paid in one call, since there's no intermediate "awaiting
 *  payment" handoff to a Manager in this case. See requests_api.py's
 *  approve_and_pay_advance. */
export async function approveAndPayAdvance(advanceId: string, comment?: string): Promise<Advance> {
  return json<Advance>(
    await fetch(`/api/my-advance-approvals/${encodeURIComponent(advanceId)}/approve-and-pay`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment }),
      ...opts,
    }),
  );
}

/** Backs the Approver Queue page's disbursement section (Finance/Approver
 *  role only). With no status, every advance actionable by this
 *  Finance/Approver (Pending routed to them + org-wide Approved-awaiting-
 *  payment). With status 'Paid' or 'Rejected', every advance org-wide
 *  already settled to that status instead - see requests_api.py's
 *  advances_awaiting_payment for why those two modes don't overlap. */
export async function fetchAdvancesAwaitingPayment(status?: 'All' | 'Paid' | 'Rejected' | 'Resubmit'): Promise<Advance[]> {
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  return json<Advance[]>(await fetch(`/api/advances/awaiting-payment${query}`, opts));
}

/** Every advance at the Finance stage for this Finance Approver - awaiting
 *  payment, paid, or rejected - which is what the dashboard's approval chart
 *  needs (a Finance rejection isn't on the Manager's approval record the
 *  other advance-approval fetches return). Just the three views of
 *  fetchAdvancesAwaitingPayment together; each is already scoped to this
 *  approver server-side. */
export async function fetchFinanceAdvanceStage(): Promise<Advance[]> {
  const [open, paid, rejected] = await Promise.all([
    fetchAdvancesAwaitingPayment(),
    fetchAdvancesAwaitingPayment('Paid'),
    fetchAdvancesAwaitingPayment('Rejected'),
  ]);
  return [...open, ...paid, ...rejected];
}

/** Disbursement - a separate step from the Manager's Approved decision,
 *  gated on the Finance/Approver role. */
export async function markAdvancePaid(advanceId: string): Promise<Advance> {
  return json<Advance>(
    await fetch(`/api/my-advances/${encodeURIComponent(advanceId)}/mark-paid`, { method: 'POST', ...opts }),
  );
}

/** The Finance Approver rejects a Manager-approved advance before paying it
 *  out - the alternative to markAdvancePaid on an "Awaiting payment" row. The
 *  employee is notified. See requests_api.py's finance_reject_advance. */
export async function rejectAdvanceAsFinance(advanceId: string, comment?: string): Promise<Advance> {
  return json<Advance>(
    await fetch(`/api/advances/${encodeURIComponent(advanceId)}/finance-reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment }),
      ...opts,
    }),
  );
}

/** Finance's Review action on an already-Manager-approved advance: save
 *  field edits - there's nothing further to approve here (it's already
 *  Approved/awaiting-payment), so this just returns the updated advance;
 *  Finance still chooses Mark paid or Reject separately afterwards. */
export async function reviewAdvanceFinance(advanceId: string, fields: object): Promise<Advance> {
  return json<Advance>(
    await fetch(`/api/advances/${encodeURIComponent(advanceId)}/finance-review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
      ...opts,
    }),
  );
}

/** Finance's Resubmit action on an already-Manager-approved advance: send
 *  it back to the employee to fix, instead of marking it paid or
 *  rejecting it. */
export async function resubmitAdvanceFinance(advanceId: string, comment: string): Promise<Advance> {
  return json<Advance>(
    await fetch(`/api/advances/${encodeURIComponent(advanceId)}/finance-resubmit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment }),
      ...opts,
    }),
  );
}

/** Employee finishes editing a Resubmit advance and resubmits it -
 *  advance-side twin of resubmitMyRequest. Updates the SAME advance
 *  document in place (unlike createAdvance, no new advance/approval
 *  record) and routes back to whichever stage sent it back. */
export async function resubmitMyAdvance(
  advanceId: string,
  fields: object,
): Promise<{ advance: Advance }> {
  return json(
    await fetch(`/api/my-advances/${encodeURIComponent(advanceId)}/resubmit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
      ...opts,
    }),
  );
}

// --- Expenses (bills/receipts) - Reimbursement claim method only. ---
export async function uploadExpense(
  travelRequestId: string,
  file: File | null,
  // date is mandatory - FX conversion (lib/fx.ts) needs the bill's own
  // transaction date to look up the historical exchange rate.
  fields: { category: ExpenseCategory; amount: number; currency: string; description?: string; date: string; vendor?: string }
    & OtherBillFieldValues,
): Promise<TravelRequestSummary> {
  const formData = new FormData();
  if (file) formData.append('file', file);
  formData.append('category', fields.category);
  formData.append('amount', String(fields.amount));
  formData.append('currency', fields.currency);
  if (fields.description) formData.append('description', fields.description);
  formData.append('date', fields.date);
  if (fields.vendor) formData.append('vendor', fields.vendor);
  // Other reimbursement bills only - never set by a travel bill.
  for (const key of ['startPlace', 'endPlace', 'purpose', 'modeOfTransport', 'buyer'] as const) {
    if (fields[key]) formData.append(key, fields[key] as string);
  }
  return json<TravelRequestSummary>(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}/expenses`, {
      method: 'POST',
      body: formData,
      ...opts,
    }),
  );
}

export async function updateExpense(
  travelRequestId: string,
  expenseId: string,
  fields: Partial<{ category: ExpenseCategory; amount: number; currency: string; description: string; date: string }>
    & OtherBillFieldValues,
): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}/expenses/${encodeURIComponent(expenseId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
      ...opts,
    }),
  );
}

export async function deleteExpense(travelRequestId: string, expenseId: string): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}/expenses/${encodeURIComponent(expenseId)}`, {
      method: 'DELETE',
      ...opts,
    }),
  );
}

export function expenseFileUrl(travelRequestId: string, expenseId: string): string {
  return `/api/my-requests/${encodeURIComponent(travelRequestId)}/expenses/${encodeURIComponent(expenseId)}/file`;
}

// --- Additional documents (plain supporting attachments) ---
// Reference files only (visa, boarding pass, itinerary, etc.) with an
// optional note - no amount/category, no duplicate check, no policy limits
// (see requests_api.py's document routes).
export async function uploadRequestDocument(
  travelRequestId: string,
  file: File,
  note?: string,
): Promise<TravelRequestSummary> {
  const formData = new FormData();
  formData.append('file', file);
  if (note) formData.append('note', note);
  return json<TravelRequestSummary>(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}/documents`, {
      method: 'POST',
      body: formData,
      ...opts,
    }),
  );
}

export async function deleteRequestDocument(travelRequestId: string, documentId: string): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}/documents/${encodeURIComponent(documentId)}`, {
      method: 'DELETE',
      ...opts,
    }),
  );
}

export function requestDocumentFileUrl(travelRequestId: string, documentId: string): string {
  return `/api/my-requests/${encodeURIComponent(travelRequestId)}/documents/${encodeURIComponent(documentId)}/file`;
}

// --- FX overrides (multi-currency reconciliation ledger) ---
// A manually-entered rate for one (currency, targetCurrency) pair - saved
// immediately, visible to anyone who can view the request (see
// requests_api.py's set_fx_override). Purely a reconciliation aid; never
// feeds settlement, the allowance or policy limits.
export async function setFxOverride(
  travelRequestId: string,
  fields: { currency: string; targetCurrency: string; rate: number; reason?: string; file?: File },
): Promise<TravelRequestSummary> {
  const formData = new FormData();
  formData.append('currency', fields.currency);
  formData.append('targetCurrency', fields.targetCurrency);
  formData.append('rate', String(fields.rate));
  if (fields.reason) formData.append('reason', fields.reason);
  if (fields.file) formData.append('file', fields.file);
  return json<TravelRequestSummary>(
    await fetch(`/api/travel-requests/${encodeURIComponent(travelRequestId)}/fx-overrides`, {
      method: 'POST',
      body: formData,
      ...opts,
    }),
  );
}

export async function removeFxOverride(travelRequestId: string, overrideId: string): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/travel-requests/${encodeURIComponent(travelRequestId)}/fx-overrides/${encodeURIComponent(overrideId)}`, {
      method: 'DELETE',
      ...opts,
    }),
  );
}

export function fxOverrideReceiptUrl(travelRequestId: string, overrideId: string): string {
  return `/api/travel-requests/${encodeURIComponent(travelRequestId)}/fx-overrides/${encodeURIComponent(overrideId)}/file`;
}

/** Saves one reconciliation-ledger row's chosen settlement currency (see
 *  TravelRequestSummary.settlementTargetByCurrency's own doc comment) -
 *  fired from CurrencyConversionCard.tsx on every change to a row's picker
 *  (or the global "Set all to…" picker, once per currency it touches), so
 *  the choice survives a reload instead of living only in that component's
 *  own state. */
export async function setSettlementTarget(
  travelRequestId: string,
  currency: string,
  targetCurrency: string,
): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/travel-requests/${encodeURIComponent(travelRequestId)}/settlement-target`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currency, targetCurrency }),
      ...opts,
    }),
  );
}

/** An approver's bill correction during Review - amount/category/currency/
 *  description only (see requests_api.py's review_edit_expense). Scoped to
 *  whichever stage (Manager or Finance) currently holds the request, not to
 *  the caller owning it like updateExpense above. */
export async function reviewEditExpense(
  travelRequestId: string,
  expenseId: string,
  fields: Partial<{ category: ExpenseCategory; amount: number; currency: string; description: string }>
    & OtherBillFieldValues,
): Promise<TravelRequestSummary> {
  return json<TravelRequestSummary>(
    await fetch(`/api/my-approvals/${encodeURIComponent(travelRequestId)}/expenses/${encodeURIComponent(expenseId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
      ...opts,
    }),
  );
}

// AI agents (agents/bill_analysis.py) - read an uploaded bill and suggest
// field values, and check it against the employee's other bills for a
// likely duplicate. Both are advisory: nothing is persisted by either
// call, the actual commit is still the uploadExpense() call above.
// Returns an array - almost always length 1, but more than one entry when
// the file contains multiple distinct bills (see agents/bill_analysis.py).
export async function analyzeExpenseFile(travelRequestId: string, file: File): Promise<ExtractedBillFields[]> {
  const formData = new FormData();
  formData.append('file', file);
  return json<ExtractedBillFields[]>(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}/expenses/analyze`, {
      method: 'POST',
      body: formData,
      ...opts,
    }),
  );
}

export async function checkExpenseDuplicate(
  travelRequestId: string,
  fields: ExtractedBillFields,
  // Every OTHER bill currently sitting as a pending (not-yet-added) card
  // in the same upload batch - without these, two copies of the same file
  // dropped in one sitting would never catch each other, since neither is
  // "already uploaded" from the backend's point of view until one is
  // actually committed. See requests_api.py's check_expense_duplicate.
  pendingCandidates: ExtractedBillFields[] = [],
): Promise<DuplicateVerdict> {
  return json<DuplicateVerdict>(
    await fetch(`/api/my-requests/${encodeURIComponent(travelRequestId)}/expenses/check-duplicate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields, pendingCandidates }),
      ...opts,
    }),
  );
}

// --- User Management (Admin only) ---
/** Only users holding an assignable role (Admin/Finance) - i.e. the people
 *  granted access to this app, not the whole directory. */
export async function fetchOrgUsers(): Promise<OrgUser[]> {
  return json<OrgUser[]>(await fetch('/api/admin/users', opts));
}

/** The full Microsoft Entra directory, for the "Add users" picker. */
export async function fetchDirectory(): Promise<OrgUser[]> {
  return json<OrgUser[]>(await fetch('/api/admin/directory', opts));
}

/** Same-origin endpoint that proxies a directory user's Graph photo by id
 *  (404 → no photo, same contract as PHOTO_URL for the signed-in user). Used
 *  by User Management's table and the Add users picker. */
export function userPhotoUrl(userId: string): string {
  return `/api/admin/users/${encodeURIComponent(userId)}/photo`;
}

export async function assignRole(userId: string, role: string): Promise<{ userId: string; roles: string[] }> {
  return json(
    await fetch(`/api/admin/users/${encodeURIComponent(userId)}/roles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
      ...opts,
    }),
  );
}

export async function revokeRole(userId: string, role: string): Promise<{ userId: string; roles: string[] }> {
  return json(
    await fetch(`/api/admin/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(role)}`, {
      method: 'DELETE',
      ...opts,
    }),
  );
}

// --- Policies (Admin edits, Admin + Finance read) ---
export async function fetchDesignations(): Promise<string[]> {
  return json<string[]>(await fetch('/api/admin/designations', opts));
}

export async function fetchPolicy(): Promise<PolicySheet | null> {
  const res = await fetch('/api/admin/policy', opts);
  if (res.status === 404) return null;
  return json<PolicySheet>(res);
}

export async function fetchPolicyHistory(): Promise<PolicySheet[]> {
  return json<PolicySheet[]>(await fetch('/api/admin/policy/history', opts));
}

export async function updatePolicy(payload: {
  fullDayHourThreshold: number;
  defaultFixedAllowance: PolicySheet['defaultFixedAllowance'];
  defaultReimbursementLimits: PolicySheet['defaultReimbursementLimits'];
  fixedAllowances: PolicySheet['fixedAllowances'];
  reimbursements: PolicySheet['reimbursements'];
}): Promise<PolicySheet> {
  return json<PolicySheet>(
    await fetch('/api/admin/policy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      ...opts,
    }),
  );
}

/** The admin-configured expense-category list (see db/policy_store.py's
 *  "Expense categories are admin-configurable" module docstring section) -
 *  global and unversioned, unlike the policy sheet itself. Read by every
 *  bill category dropdown/label and the per-designation rate editor. */
export async function fetchExpenseCategories(): Promise<string[]> {
  const data = await json<{ categories: string[] }>(await fetch('/api/admin/expense-categories', opts));
  return data.categories;
}

/** Removing a category that's still used in policy limits or existing
 *  bills is ALLOWED, not blocked - call previewCategoryRemoval first (see
 *  below) to confirm with the admin, since this auto-strips the category
 *  from every policy rate's limits (a new policy version) and silently
 *  saves if called without that confirmation step. */
export async function updateExpenseCategories(categories: string[]): Promise<string[]> {
  const data = await json<{ categories: string[] }>(
    await fetch('/api/admin/expense-categories', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ categories }),
      ...opts,
    }),
  );
  return data.categories;
}

export interface CategoryRemovalUsage {
  policyRateCount: number;
  designationCount: number;
  billCount: number;
}

/** Exact usage counts for a candidate set of categories about to be
 *  removed - drives Policies.tsx's confirmation dialog wording before a
 *  Save that drops any category. */
export async function previewCategoryRemoval(
  categories: string[],
): Promise<CategoryRemovalUsage> {
  return json<CategoryRemovalUsage>(
    await fetch('/api/admin/expense-categories/preview-removal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ categories }),
      ...opts,
    }),
  );
}

// --- Audit Logs (Admin only) ---
/** One page of audit entries (newest first) plus the total matching count -
 *  the backend pages via OFFSET/LIMIT rather than returning everything at
 *  once, so this stays cheap as the org's audit trail grows (see
 *  admin_api.py's list_audit_logs / db/audit_store.py's list_recent). */
export interface AuditLogPage {
  logs: AuditLogEntry[];
  total: number;
}

export async function fetchAuditLogs(filters?: {
  offset?: number;
  limit?: number;
  action?: string;
  /** Groups by a shared dotted action prefix (e.g. "approval." also matches
   *  finance_approval./advance_approval.) - mutually exclusive with actionIn. */
  actionPrefix?: string;
  /** A fixed list of exact action strings that don't share one prefix (e.g.
   *  travel_request.submitted + advance.raised) - mutually exclusive with actionPrefix. */
  actionIn?: string[];
  actorId?: string;
  /** Free-text search over actor display name/email - resolved server-side
   *  against UserStore first (audit-log docs only store actorId, never a
   *  name), then the audit query is filtered to the matching actor ids. */
  actorSearch?: string;
  since?: string;
  until?: string;
}): Promise<AuditLogPage> {
  const params = new URLSearchParams();
  if (filters?.offset) params.set('offset', String(filters.offset));
  if (filters?.limit) params.set('limit', String(filters.limit));
  if (filters?.action) params.set('action', filters.action);
  if (filters?.actionPrefix) params.set('actionPrefix', filters.actionPrefix);
  for (const a of filters?.actionIn ?? []) params.append('actionIn', a);
  if (filters?.actorId) params.set('actorId', filters.actorId);
  if (filters?.actorSearch) params.set('actorSearch', filters.actorSearch);
  if (filters?.since) params.set('since', filters.since);
  if (filters?.until) params.set('until', filters.until);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return json<AuditLogPage>(await fetch(`/api/admin/audit-logs${qs}`, opts));
}

// --- Dashboard volume chart (Admin only) ---
export async function fetchAdminDashboardVolume(): Promise<{ requests: TravelRequestSummary[]; advances: Advance[] }> {
  return json(await fetch('/api/admin/dashboard/volume', opts));
}
